/**
 * Create a task or note from editor text and drop a reference to it into the
 * document. Shared by the toolbar's Create task / Create note popovers and the
 * selection bar (#279), so both stay one flow.
 */

import type { Editor } from '@tiptap/react';
import { apiPost } from '../../lib/api';
import { startActionGroup, endActionGroup } from '../../lib/actionTracking';
import { promote as promoteFromScratchpad } from '../../lib/scratchpads';

export type CreatableKind = 'task' | 'note';

export interface CreateContext {
  meetingInstanceId?: string;
  scratchpadId?: string;
}

export async function createEntityFromText(
  kind: CreatableKind,
  title: string,
  { meetingInstanceId, scratchpadId }: CreateContext,
): Promise<{ id: string; title: string }> {
  // Creating the item and the document's autosave (selection replaced by a
  // reference) are one user action: group them so one undo reverses both (#278).
  startActionGroup();
  try {
    return await createEntity(kind, title, { meetingInstanceId, scratchpadId });
  } catch (err) {
    endActionGroup();
    throw err;
  }
}

async function createEntity(
  kind: CreatableKind,
  title: string,
  { meetingInstanceId, scratchpadId }: CreateContext,
): Promise<{ id: string; title: string }> {
  // Inside a scratchpad, go through the promote endpoint: it creates the
  // entity AND its provenance link back to the pad in one transaction.
  // POST /tasks would create the task and silently drop the link.
  if (scratchpadId) {
    const r = await promoteFromScratchpad(scratchpadId, { target: kind, text: title });
    return { id: r.entity_id, title: r.label };
  }
  return apiPost<{ id: string; title: string }>(kind === 'task' ? '/tasks' : '/notes', {
    title,
    source_meeting_id: meetingInstanceId,
  });
}

export function referenceNode(kind: CreatableKind, created: { id: string; title: string }) {
  return kind === 'task'
    ? { type: 'taskReference', attrs: { id: created.id, title: created.title, checked: false } }
    : { type: 'noteReference', attrs: { id: created.id, title: created.title } };
}

/**
 * Put the reference into the document, replacing `range` when asked.
 *
 * The range has to be captured before any title input takes focus: by commit
 * time the editor selection is no longer the one the user made.
 */
export function insertReference(
  editor: Editor,
  node: { type: string; attrs: Record<string, unknown> },
  range: { from: number; to: number } | undefined,
  replace: boolean,
): void {
  const chain = editor.chain().focus();
  if (range && !replace) {
    // Keep the text: put the reference right after it. A plain insertContent
    // here would land on the still-active selection and overwrite the text.
    chain.insertContentAt(range.to, node).run();
    return;
  }
  if (range) {
    chain.deleteRange(range);
  }
  chain.insertContent(node).run();
}
